import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Modal,
    Typography,
    useTheme,
} from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { DossierOpenSearch, useFormaliteService } from '@europrocurement/l2d-domain';
import { CollectionDeDossierApiApiCollectionsDossiersIdPatchRequest } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import DossierActionContext from '../../../providers/DossierActionContext';
import {
    UPDATE_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtxDispatch,
} from '../../../providers/CurrentDossierProvider';

const DossierUrgentModal: React.FunctionComponent = function () {
    const { currentAction, cancelCurrentAction, dossiers } = useContext(DossierActionContext);
    const { collectionApi, getOpenSearchDossiers } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [submitingProcess, setSubmittingProcess] = useState<boolean>(false);
    const [dossier, setDossier] = useState<DossierOpenSearch | undefined>();
    const dispatchCurrentDossier = useCurrentDossierCtxDispatch();

    useEffect(() => {
        setModalOpen(currentAction === 'urgent');
    }, [currentAction]);

    useEffect(() => {
        if (dossiers.length === 1) {
            setDossier(dossiers[0]);
        }
    }, [dossiers]);

    const handleCloseModal = () => {
        setModalOpen(false);
        cancelCurrentAction();
    };

    const handleUpdateUrgent = () => {
        if (submitingProcess) {
            return;
        }
        if (dossier && dossier.collectionId) {
            setSubmittingProcess(true);
            let searchCollectionTimeout: number | undefined;
            clearTimeout(searchCollectionTimeout);
            const requestParameters: CollectionDeDossierApiApiCollectionsDossiersIdPatchRequest = {
                id: dossier.collectionId.toString(),
                collectionDeDossierCollectiondossierUpdate: {
                    urgent: true,
                },
            };

            searchCollectionTimeout = setTimeout(() => {
                collectionApi
                    .apiCollectionsDossiersIdPatch(requestParameters)
                    .then((res) => {
                        if (res.status === 200) {
                            enqueueSnackbar('Le dossier est bien marqué comme urgent');
                            dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
                            if (
                                dispatchCurrentDossier &&
                                typeof dispatchCurrentDossier !== 'undefined'
                            ) {
                                dispatchCurrentDossier({
                                    type: UPDATE_CURRENT_DOSSIER_CTX,
                                    payload: {
                                        dossierId: dossier.id as number,
                                        data: {
                                            ...dossier,
                                            critical: true,
                                        },
                                    },
                                });
                            }
                        } else {
                            enqueueSnackbar(
                                'Une erreur est survenue lors du marquage de dossier en urgent',
                                { variant: 'error' },
                            );
                        }
                        setSubmittingProcess(false);
                        handleCloseModal();
                    })
                    .catch((err) => {
                        enqueueSnackbar(
                            'Une erreur est survenue lors du marquage de dossier en urgent',
                            { variant: 'error' },
                        );
                        console.log(err.message);
                        setSubmittingProcess(false);
                        handleCloseModal();
                    });
            }, 300);
        }
    };

    if (dossier) {
        return (
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="create-modal-title"
                aria-describedby="create-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 550,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: '12px',
                    }}
                >
                    <IconButton
                        aria-label="close"
                        data-testid="close-button-test"
                        onClick={handleCloseModal}
                        sx={{
                            fontSize: '16px',
                            position: 'absolute',
                            right: '20px',
                            top: '35px',
                            color: palette.grey[500],
                        }}
                    >
                        <FaOptionIcon icon={['fas', 'close']} />
                    </IconButton>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            paddingTop: '24px',
                        }}
                    >
                        <Typography variant="l2dh6">Marquer comme urgent</Typography>
                        <Typography variant="l2dbodym">
                            Êtes-vous sûr de passer le dossier #{dossier.numero} en urgent ?
                        </Typography>
                        <Box
                            sx={{
                                padding: '26px 30px',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                {submitingProcess ? (
                                    <Button
                                        variant="contained"
                                        sx={{
                                            padding: '16px 24px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            minWidth: '6rem',
                                            fontSize: '16px',
                                        }}
                                    >
                                        <CircularProgress
                                            color="info"
                                            sx={{
                                                maxHeight: '15px',
                                                maxWidth: '15px',
                                                color: 'common.white',
                                            }}
                                        />
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleUpdateUrgent}
                                        sx={{ padding: '16px 24px', fontSize: '16px' }}
                                        variant="contained"
                                    >
                                        Valider
                                    </Button>
                                )}
                                <Button
                                    sx={{ padding: '16px 24px', fontSize: '16px' }}
                                    variant="outlined"
                                >
                                    Annuler
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        );
    }
    return null;
};

export default DossierUrgentModal;
